// Import the dashboard types from local models
import type { SASMainDashboard } from './models/sASMainDashboard';
import type { SASEGDashboard } from './models/sASEGDashboard';
import type { SASDIDashboard } from './models/sASDIDashboard';
import type { SASCodeDashboard } from './models/sASCodeDashboard';

// Dashboard navigation enum
export enum EDashboard {
  MAIN = 'main',
  CODE = 'code',
  EG = 'eg',
  DI = 'di',
}

// Export the dashboard types with aliases
export type MainDashboard = SASMainDashboard;
export type EGDashboard = SASEGDashboard;
export type DIDashboard = SASDIDashboard;
export type CodeDashboard = SASCodeDashboard;

// DashboardData interface for our unified API response
export interface DashboardData {
  main?: MainDashboard | null;
  eg?: EGDashboard | null;
  di?: DIDashboard | null;
  code?: CodeDashboard | null;
}

// Chart Data Types
// These types are defined locally rather than using the generated models because:
// 1. It decouples the dashboard from API-generated structures
// 2. Allows customization for dashboard-specific needs (e.g., adding 'id' to PieChartDataRow)
// 3. Provides stability even if generated models change
// 4. Simplifies the types for easier use in components

/**
 * Interface for a row of data in a bar chart.
 *
 * @interface BarChartDataRow
 *
 * @property {string | number} category - The category aka x-axis value.
 *    This can be a string, number, or Date.
 * @property {number} value - The value of the data in the category.
 */
export interface BarChartDataRow {
  category: string | number;
  value: number;
}

/**
 * Interface for the entire data necessary to render a bar chart.
 *
 * @interface BarChartData
 *
 * @property {BarChartDataRow[]} data - The data to render in the bar chart.
 */
export type BarChartData = BarChartDataRow[];

/**
 * Interface for a row of data in a bar chart.
 *
 * @interface StackedBarChartDataRow
 *
 * @property {string | number} category - The category aka x-axis value.
 *    This can be a string, number, or Date.
 * @property {string} sub_category - The sub category aka the key for the second dimension.
 * @property {number} value - The value of the data in the category.
 */
export interface StackedBarChartDataRow {
  category: string | number;
  sub_category: string;
  value: number;
}

/**
 * Interface for the entire data necessary to render a stacked bar chart.
 *
 * @interface StackedBarChartData
 *
 * @property {StackedBarChartDataRow[]} data - The data to render in the stacked bar chart.
 */
export type StackedBarChartData = Array<StackedBarChartDataRow>;

//Treemap Chart
/**
 * Interface for a leaf data node in a treemap.
 */
export interface TreemapLeaf {
  name: string;
  value: number;
}

/**
 * Interface for a non-leaf node in a Treemap.
 *
 * @interface TreemapNode
 *
 * @property {string} name - The name of the node (similar to category in barchart).
 * @property {number | null} value - The value of the node, may not exist.
 * @property {TreemapChild[]} children - An optional array of sub categories with values.
 */
export interface TreemapNode {
  name: string;
  children: (TreemapNode | TreemapLeaf)[];
}

/**
 * Treemap data type
 */
export type TreemapData = TreemapNode;

/**
 * Interface for a row of data in a pie chart.
 *
 * @interface PieChartDataRow
 *
 * @property {string} category - The category (defines label as well).
 * @property {number} value - The value of the data in the category.
 */
export interface PieChartDataRow {
  id: any;
  category: string;
  value: number;
}

/**
 * Interface for the entire data necessary to render a pie chart.
 *
 * @interface PieChartData
 *
 * @property {PieChartDataRow[]} data - The data to render in the pie chart.
 */
export type PieChartData = Array<PieChartDataRow>;

/**
 * Interface for a reference line in a histogram.
 *
 * @interface HistogramReferenceLine
 *
 * @property {string | number | Date} category_value - The value of the category (x axis value)
 *  for the reference line.
 */
export interface HistogramReferenceLine {
  category_value: string | number | Date;
}

/**
 * Interface for the entire data necessary to render a histogram.
 *
 * @interface HistogramData
 *
 * @property {BarChartData} data - The data to render in the histogram (barchart essentially).
 * @property {HistogramReferenceLine | null} reference_line - The reference line for the histogram.
 */
export interface HistogramData {
  barChartData: BarChartData;
  reference_line: HistogramReferenceLine | null;
}

export type SASDashboardBackendChartDataType =
  | StackedBarChartData
  | BarChartData
  | PieChartData
  | TreemapData
  | HistogramData
  | number
  | null
  | string;

export interface SASDashboardBackendDataType {
  [key: string]: SASDashboardBackendChartDataType;
}

export interface SASEGDashboardType {
  codeUniqueness: number;
  customFormatsUsed: number;
  cyclomaticComplexity: HistogramData;
  dataStepsByCategory: PieChartData;
  fileTypes: StackedBarChartData;
  flowBySizeInTokens: HistogramData;
  flowLastRun: HistogramData;
  flowLastRunPie: PieChartData;
  flowsByWorkloadTypes: BarChartData;
  flowUniquness: PieChartData;
  libraryEngines: BarChartData;
  linesOfCode: number;
  programBySizeInTokens: HistogramData;
  SASEgTasks: BarChartData;
  SQLByCategory: PieChartData;
  sqlPassThroughShare: PieChartData;
  statementsByCategory: PieChartData;
  tokensByWorkloadTypes: TreemapData;
  tokensUnsupported?: number | null;
  top3Users: BarChartData;
  top5BuiltinFormats: BarChartData;
  top5fileNameDevices: BarChartData;
  top5fileNameExtensions: BarChartData;
  top5Functions: BarChartData;
  top5Procs: BarChartData;
  topMacros: BarChartData;
  totalCodeTaskPrograms: number;
  totalFlows: number;
  totalTokens: number;
  uniqueMacros: number;
  uniqueUsers: number;
}

export interface SASMainDashboardType {
  errorsInTokenCounts: boolean;
  executablesByType: PieChartData;
  missingUnsupportedTokens: boolean;
  notifications: Record<'INFO' | 'WARNING' | 'ERROR', number>;
  sourceFilesTotal: number;
  tokensByWorkloadTypes: TreemapData;
  tokensLicense: number;
  tokensTotal: number;
  tokensUnsupported?: number | null;
}

export interface SASDIDashboardType {
  codeUniqueness: number;
  customFormatsUsed: number;
  cyclomaticComplexity: HistogramData;
  datasetCountTypes: BarChartData;
  dataStepsByCategory: PieChartData;
  DITransformsCategotry: PieChartData;
  DITransformsTypes: BarChartData;
  jobBySizeInTokens: HistogramData;
  jobsByWorkloadTypes: BarChartData;
  jobUniquness: PieChartData;
  libraryEngines: BarChartData;
  linesOfCode: number;
  programBySizeInTokens: HistogramData;
  SQLByCategory: PieChartData;
  sqlPassThroughShare: PieChartData;
  statementsByCategory: PieChartData;
  tokensByWorkloadTypes: TreemapData;
  tokensUnsupported?: number | null;
  top5BuiltinFormats: BarChartData;
  top5fileNameDevices: BarChartData;
  top5fileNameExtensions: BarChartData;
  top5Functions: BarChartData;
  top5Procs: BarChartData;
  topMacros: BarChartData;
  totalJobs: number;
  totalTokens: number;
  totalUserWrittenPrograms: number;
  uniqueMacros: number;
}

export interface SASCodeDashboardType {
  codeUniqueness: number;
  customFormatsUsed: number;
  cyclomaticComplexity: HistogramData;
  dataStepsByCategory: PieChartData;
  libraryEngines: BarChartData;
  linesOfCode: number;
  programBySizeInTokens: HistogramData;
  programsByWorkloadTypes: BarChartData;
  programUniqueness: PieChartData;
  SQLByCategory: PieChartData;
  sqlPassThroughShare: PieChartData;
  statementsByCategory: PieChartData;
  tokensByWorkloadTypes: TreemapData;
  tokensUnsupported?: number | null;
  top5BuiltinFormats: BarChartData;
  top5fileNameDevices: BarChartData;
  top5fileNameExtensions: BarChartData;
  top5Functions: BarChartData;
  top5Procs: BarChartData;
  topMacros: BarChartData;
  totalPrograms: number;
  totalTokens: number;
  uniqueMacros: number;
}

// Nivo interfaces
/**
 * Interface for a row of data in a Nivo Stacked Bar Chart.
 * Nivo requires denormalized data for stacked bar charts, where each sub category becomes a separate key.
 *
 * @interface NivoStackedBarChartData
 *
 * @property {string} id - The main category aka x-axis value.
 * @property {string | number} [key: string]: string | number - The values (bar height) for each second dimension key.
 */
export interface NivoStackedBarChartData {
  id: string;
  [key: string]: string | number; // includes the second dimension keys
}

export type NivoDashboardBackendChartDataType =
  | NivoStackedBarChartData[]
  | BarChartData
  | PieChartData
  | TreemapData
  | HistogramData
  | number
  | string;

export interface NivoDashboardBackendDataType {
  [key: string]: NivoDashboardBackendChartDataType;
}

export interface NivoSASEGDashboardType {
  codeUniqueness: number;
  customFormatsUsed: number;
  cyclomaticComplexity: HistogramData;
  dataStepsByCategory: PieChartData;
  fileTypes: NivoStackedBarChartData[];
  flowBySizeInTokens: HistogramData;
  flowLastRun: HistogramData;
  flowLastRunPie: PieChartData;
  flowsByWorkloadTypes: BarChartData;
  flowUniquness: PieChartData;
  libraryEngines: BarChartData;
  linesOfCode: number;
  programBySizeInTokens: HistogramData;
  SASEgTasks: BarChartData;
  SQLByCategory: PieChartData;
  sqlPassThroughShare: PieChartData;
  statementsByCategory: PieChartData;
  tokensByWorkloadTypes: TreemapData;
  tokensUnsupported?: number | null;
  top3Users: BarChartData;
  top5BuiltinFormats: BarChartData;
  top5fileNameDevices: BarChartData;
  top5fileNameExtensions: BarChartData;
  top5Functions: BarChartData;
  top5Procs: BarChartData;
  topMacros: BarChartData;
  totalCodeTaskPrograms: number;
  totalFlows: number;
  totalTokens: number;
  uniqueMacros: number;
  uniqueUsers: number;
}

export interface NivoMainDashboardType {
  errorsInTokenCounts: boolean;
  executablesByType: PieChartData;
  missingUnsupportedTokens: boolean;
  notifications: Record<'INFO' | 'WARNING' | 'ERROR', number>;
  sourceFilesTotal: number;
  tokensByWorkloadTypes: TreemapData;
  tokensLicense: number;
  tokensTotal: number;
  tokensUnsupported?: number | null;
}

export interface NivoDIDashboardType {
  codeUniqueness: number;
  customFormatsUsed: number;
  cyclomaticComplexity: HistogramData;
  datasetCountTypes: BarChartData;
  dataStepsByCategory: PieChartData;
  DITransformsCategotry: PieChartData;
  DITransformsTypes: BarChartData;
  jobBySizeInTokens: HistogramData;
  jobsByWorkloadTypes: BarChartData;
  jobUniquness: PieChartData;
  libraryEngines: BarChartData;
  linesOfCode: number;
  programBySizeInTokens: HistogramData;
  SQLByCategory: PieChartData;
  sqlPassThroughShare: PieChartData;
  statementsByCategory: PieChartData;
  tokensByWorkloadTypes: TreemapData;
  tokensUnsupported?: number | null;
  top5BuiltinFormats: BarChartData;
  top5fileNameDevices: BarChartData;
  top5fileNameExtensions: BarChartData;
  top5Functions: BarChartData;
  top5Procs: BarChartData;
  topMacros: BarChartData;
  totalJobs: number;
  totalTokens: number;
  totalUserWrittenPrograms: number;
  uniqueMacros: number;
}

export interface NivoCodeDashboardType {
  codeUniqueness: number;
  customFormatsUsed: number;
  cyclomaticComplexity: HistogramData;
  dataStepsByCategory: PieChartData;
  libraryEngines: BarChartData;
  linesOfCode: number;
  programBySizeInTokens: HistogramData;
  programsByWorkloadTypes: BarChartData;
  programUniqueness: PieChartData;
  SQLByCategory: PieChartData;
  sqlPassThroughShare: PieChartData;
  statementsByCategory: PieChartData;
  tokensByWorkloadTypes: TreemapData;
  tokensUnsupported?: number | null;
  top5BuiltinFormats: BarChartData;
  top5fileNameDevices: BarChartData;
  top5fileNameExtensions: BarChartData;
  top5Functions: BarChartData;
  top5Procs: BarChartData;
  topMacros: BarChartData;
  totalPrograms: number;
  totalTokens: number;
  uniqueMacros: number;
}